import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { Checkbox, SelectInput, TextInput } from "@/components/ui/Field";
import { ApiError } from "@/lib/apiClient";
import {
  copyGdLayoutConfig,
  defaultGdLayoutConfig,
  fetchGdLayoutPreviewPdf,
  fetchGdBriefpapierPreviewBlob,
  GD_ADRESSE_ZEILENABSTAND_OPTIONS,
  GD_TEXT_ZEILENABSTAND_OPTIONS,
  GD_DOKUMENT_TYP_OPTIONS,
  GD_FONT_OPTIONS,
  GD_FONT_SIZE_OPTIONS,
  gdLayoutSectionsForTyp,
  getGdLayoutConfig,
  normalizeGdLayoutConfig,
  saveGdLayoutConfig,
  uploadGdBriefpapier,
  deleteGdBriefpapier,
  type GdLayoutConfig,
  type GdLayoutSection,
  type GdLayoutTyp,
  type GdPageLayout,
  type GdPreviewPage,
} from "@/lib/gdLayoutConfig";
import {
  emptyGdStartnummern,
  GD_NUMMER_TYPEN,
  getGdNummernEinstellungen,
  gdNummerTypLabel,
  previewNaechsteNummer,
  saveGdNummernEinstellungen,
  startnummernFromEinstellungen,
  type GdNummernEinstellungen,
  type GdStartnummernPayload,
} from "@/lib/gdNummernConfig";
import { toast } from "@/lib/toast";
import {
  GdErsteSeiteContentPanel,
  GdQrSignaturPanel,
  GdZusaetzlicheOptionenPanel,
  PositionstextFields,
} from "./GdLayoutContentPanels";
import "./gd-layout-configurator.css";

const GD_LAYOUT_EDITABLE_TYP_OPTIONS = GD_DOKUMENT_TYP_OPTIONS.filter(
  (opt) => opt.value !== "stundennachweis",
);

function num(value: string): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function PageLayoutFields({
  title,
  layout,
  onChange,
}: {
  title: string;
  layout: GdPageLayout;
  onChange: (next: GdPageLayout) => void;
}) {
  function patch(partial: Partial<GdPageLayout>) {
    onChange({ ...layout, ...partial });
  }

  return (
    <div className="gd-layout-config__field-group">
      <h3>{title}</h3>
      <p className="gd-layout-config__hint">Seitenränder und Position des Hauptblocks in Millimetern.</p>
      <div className="gd-layout-config__field-group">
        <h3>Seitenränder</h3>
        <div className="gd-layout-config__grid-4">
          <TextInput
            label="Oben"
            type="number"
            value={String(layout.margin_top_mm)}
            onChange={(e) => patch({ margin_top_mm: num(e.target.value) })}
          />
          <TextInput
            label="Unten"
            type="number"
            value={String(layout.margin_bottom_mm)}
            onChange={(e) => patch({ margin_bottom_mm: num(e.target.value) })}
          />
          <TextInput
            label="Links"
            type="number"
            value={String(layout.margin_left_mm)}
            onChange={(e) => patch({ margin_left_mm: num(e.target.value) })}
          />
          <TextInput
            label="Rechts"
            type="number"
            value={String(layout.margin_right_mm)}
            onChange={(e) => patch({ margin_right_mm: num(e.target.value) })}
          />
        </div>
      </div>
      <div className="gd-layout-config__field-group">
        <h3>Hauptblock</h3>
        <div className="gd-layout-config__grid-4">
          <TextInput
            label="Oben"
            type="number"
            value={String(layout.block_top_mm)}
            onChange={(e) => patch({ block_top_mm: num(e.target.value) })}
          />
          <TextInput
            label="Links"
            type="number"
            value={String(layout.block_left_mm)}
            onChange={(e) => patch({ block_left_mm: num(e.target.value) })}
          />
          <TextInput
            label="Rechts"
            type="number"
            value={String(layout.block_right_mm)}
            onChange={(e) => patch({ block_right_mm: num(e.target.value) })}
          />
        </div>
      </div>
    </div>
  );
}

function BriefpapierUpload({
  label,
  briefpapierId,
  onUploaded,
  onRemove,
  uploading,
  onUploadStart,
  onUploadEnd,
  dokumentTyp,
  seite,
}: {
  label: string;
  briefpapierId: number | null;
  onUploaded: (id: number) => void;
  onRemove: () => void;
  uploading: boolean;
  onUploadStart: () => void;
  onUploadEnd: () => void;
  dokumentTyp: GdLayoutTyp;
  seite: GdPreviewPage;
}) {
  const [thumbUrl, setThumbUrl] = useState<string | null>(null);
  const [thumbError, setThumbError] = useState(false);

  useEffect(() => {
    if (!briefpapierId) {
      setThumbUrl(null);
      setThumbError(false);
      return;
    }
    let objectUrl: string | null = null;
    let cancelled = false;
    setThumbError(false);
    void fetchGdBriefpapierPreviewBlob(briefpapierId)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setThumbUrl(objectUrl);
      })
      .catch(() => {
        if (!cancelled) {
          setThumbUrl(null);
          setThumbError(true);
        }
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [briefpapierId]);

  return (
    <div className="gd-layout-config__field-group">
      <h3>{label}</h3>
      <p className="gd-layout-config__hint">
        PNG, JPEG, WebP oder PDF (max. 15 MB). Das Briefpapier wird als Hintergrund der Seite gerendert.
      </p>
      <div className="gd-layout-config__briefpapier">
        {thumbUrl ? (
          <img src={thumbUrl} alt="" className="gd-layout-config__briefpapier-thumb" />
        ) : briefpapierId && thumbError ? (
          <div className="gd-layout-config__briefpapier-empty gd-layout-config__briefpapier-empty--error">
            Vorschau fehlgeschlagen
          </div>
        ) : briefpapierId ? (
          <div className="gd-layout-config__briefpapier-empty">
            <Spinner />
          </div>
        ) : (
          <div className="gd-layout-config__briefpapier-empty">Kein Briefpapier</div>
        )}
        <div className="flex flex-col gap-2">
          <label className="eh-label">
            Datei hochladen
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp,application/pdf"
              className="eh-input mt-1"
              disabled={uploading}
              onChange={async (e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (!file) return;
                onUploadStart();
                let localPreview: string | null = null;
                if (file.type.startsWith("image/")) {
                  localPreview = URL.createObjectURL(file);
                  setThumbUrl(localPreview);
                  setThumbError(false);
                }
                try {
                  const meta = await uploadGdBriefpapier(dokumentTyp, seite, file);
                  if (localPreview) URL.revokeObjectURL(localPreview);
                  onUploaded(meta.id);
                  toast.success("Briefpapier hochgeladen.");
                } catch (err) {
                  if (localPreview) URL.revokeObjectURL(localPreview);
                  setThumbUrl(null);
                  toast.error(err instanceof ApiError ? err.message : "Upload fehlgeschlagen.");
                } finally {
                  onUploadEnd();
                }
              }}
            />
          </label>
          {briefpapierId ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={uploading}
              onClick={async () => {
                try {
                  await deleteGdBriefpapier(briefpapierId);
                  onRemove();
                  toast.success("Briefpapier entfernt.");
                } catch (err) {
                  toast.error(err instanceof ApiError ? err.message : "Löschen fehlgeschlagen.");
                }
              }}
            >
              Entfernen
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/** Deep-Link `?typ=mahnung` (z. B. aus dem Mahnwesen) öffnet direkt den passenden Dokumenttyp. */
function initialDokumentTyp(): GdLayoutTyp {
  if (typeof window === "undefined") return "rechnung";
  const typ = new URLSearchParams(window.location.search).get("typ");
  return GD_DOKUMENT_TYP_OPTIONS.some((o) => o.value === typ) ? (typ as GdLayoutTyp) : "rechnung";
}

export function GdLayoutConfiguratorPage() {
  const [dokumentTyp, setDokumentTyp] = useState<GdLayoutTyp>(initialDokumentTyp);
  const [layout, setLayout] = useState<GdLayoutConfig>(() => defaultGdLayoutConfig(initialDokumentTyp()));
  const [section, setSection] = useState<GdLayoutSection>("gestaltung");
  const [contentAmount, setContentAmount] = useState(3);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingBriefpapier, setUploadingBriefpapier] = useState(false);
  const [copySource, setCopySource] = useState<GdLayoutTyp>("angebot");
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewHasContent, setPreviewHasContent] = useState(false);
  const [previewSyncing, setPreviewSyncing] = useState(false);
  const [nummern, setNummern] = useState<GdNummernEinstellungen | null>(null);
  const [startnummern, setStartnummern] = useState<GdStartnummernPayload>(() => emptyGdStartnummern());
  const [nummernLoading, setNummernLoading] = useState(false);
  const [nummernSaving, setNummernSaving] = useState(false);
  const previewTimer = useRef<number | null>(null);
  const previewRequestId = useRef(0);
  const previewAbortRef = useRef<AbortController | null>(null);
  const previewObjectUrlRef = useRef<string | null>(null);

  const isStundennachweis = dokumentTyp === "stundennachweis";
  const visibleSections = useMemo(() => gdLayoutSectionsForTyp(dokumentTyp), [dokumentTyp]);

  const loadLayout = useCallback(async (typ: GdLayoutTyp) => {
    setLoading(true);
    setError(null);
    if (typ === "stundennachweis") {
      setLayout(defaultGdLayoutConfig(typ));
      setLoading(false);
      return;
    }
    try {
      const cfg = await getGdLayoutConfig(typ);
      setLayout(cfg);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Layout konnte nicht geladen werden.");
      setLayout(normalizeGdLayoutConfig(null, typ));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadNummern = useCallback(async () => {
    setNummernLoading(true);
    try {
      const data = await getGdNummernEinstellungen();
      setNummern(data);
      setStartnummern(startnummernFromEinstellungen(data));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Nummernkreise konnten nicht geladen werden.");
    } finally {
      setNummernLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadLayout(dokumentTyp);
  }, [dokumentTyp, loadLayout]);

  useEffect(() => {
    if (isStundennachweis) return;
    // Quelle darf nie dem aktuellen Dokumenttyp entsprechen – sonst zeigt das
    // Select optisch einen anderen Typ, behält aber den ungültigen Value.
    if (copySource === dokumentTyp || copySource === "stundennachweis") {
      const fallback = GD_LAYOUT_EDITABLE_TYP_OPTIONS.find((opt) => opt.value !== dokumentTyp)?.value;
      if (fallback) setCopySource(fallback);
    }
  }, [dokumentTyp, copySource, isStundennachweis]);

  useEffect(() => {
    if (isStundennachweis) return;
    if (!visibleSections.some((item) => item.id === section)) {
      setSection(visibleSections[0]?.id ?? "gestaltung");
    }
  }, [dokumentTyp, isStundennachweis, section, visibleSections]);

  useEffect(() => {
    if (section === "nummern") {
      void loadNummern();
    }
  }, [section, loadNummern]);

  useEffect(() => {
    if (loading) return;
    if (previewTimer.current) window.clearTimeout(previewTimer.current);
    previewTimer.current = window.setTimeout(() => {
      previewAbortRef.current?.abort();
      const controller = new AbortController();
      previewAbortRef.current = controller;
      const requestId = previewRequestId.current + 1;
      previewRequestId.current = requestId;
      setPreviewSyncing(true);

      void fetchGdLayoutPreviewPdf(dokumentTyp, layout, contentAmount, controller.signal)
        .then((blob) => {
          if (requestId !== previewRequestId.current) return;
          if (previewObjectUrlRef.current) URL.revokeObjectURL(previewObjectUrlRef.current);
          const url = URL.createObjectURL(blob);
          previewObjectUrlRef.current = url;
          setPreviewUrl(url);
          setPreviewHasContent(true);
        })
        .catch((err: unknown) => {
          if (controller.signal.aborted) return;
          if (requestId !== previewRequestId.current) return;
          if (err instanceof DOMException && err.name === "AbortError") return;
        })
        .finally(() => {
          if (requestId !== previewRequestId.current) return;
          setPreviewSyncing(false);
        });
    }, 550);
    return () => {
      if (previewTimer.current) window.clearTimeout(previewTimer.current);
    };
  }, [dokumentTyp, layout, contentAmount, loading]);

  useEffect(() => {
    return () => {
      previewAbortRef.current?.abort();
      if (previewObjectUrlRef.current) {
        URL.revokeObjectURL(previewObjectUrlRef.current);
        previewObjectUrlRef.current = null;
      }
    };
  }, []);

  const sectionTitle = useMemo(() => {
    if (isStundennachweis) return "Stundennachweis-Vorlage";
    return visibleSections.find((item) => item.id === section)?.label ?? "Einstellungen";
  }, [isStundennachweis, section, visibleSections]);

  async function handleSave() {
    if (isStundennachweis) return;
    setSaving(true);
    setError(null);
    try {
      const saved = await saveGdLayoutConfig(dokumentTyp, layout);
      setLayout(saved);
      toast.success("Layout gespeichert.");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  async function handleCopyFromType() {
    if (isStundennachweis) return;
    const quellTyp =
      copySource !== dokumentTyp && copySource !== "stundennachweis"
        ? copySource
        : GD_LAYOUT_EDITABLE_TYP_OPTIONS.find((opt) => opt.value !== dokumentTyp)?.value;
    if (!quellTyp || quellTyp === dokumentTyp) {
      toast.error("Bitte einen anderen Dokumenttyp als Quelle wählen.");
      return;
    }
    if (quellTyp !== copySource) setCopySource(quellTyp);
    setSaving(true);
    try {
      const copied = await copyGdLayoutConfig(dokumentTyp, quellTyp);
      setLayout(copied);
      const quellLabel =
        GD_DOKUMENT_TYP_OPTIONS.find((opt) => opt.value === quellTyp)?.label ?? quellTyp;
      toast.success(`Layout von ${quellLabel} übernommen.`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Kopieren fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveNummern() {
    setNummernSaving(true);
    setError(null);
    try {
      const saved = await saveGdNummernEinstellungen(startnummern);
      setNummern(saved);
      setStartnummern(startnummernFromEinstellungen(saved));
      toast.success("Startnummern gespeichert.");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Startnummern konnten nicht gespeichert werden.");
    } finally {
      setNummernSaving(false);
    }
  }

  function renderSectionPanel() {
    if (isStundennachweis) {
      return (
        <>
          <p className="gd-layout-config__hint gd-layout-config__info-box">
            Der Stundennachweis wird über das Jinja-Template gerendert (inkl. QR-Codes und
            Unterschriftenfeldern). Hier gibt es keine Layout-Einstellungen – nur eine
            Live-Vorschau der aktuellen Vorlage.
          </p>
          <div className="gd-layout-config__grid-2">
            <SelectInput
              label="Dokumenttyp"
              value={dokumentTyp}
              onChange={(e) => setDokumentTyp(e.target.value as GdLayoutTyp)}
            >
              {GD_DOKUMENT_TYP_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </SelectInput>
          </div>
        </>
      );
    }

    if (section === "nummern") {
      return (
        <>
          <p className="gd-layout-config__hint">
            Startnummer pro Dokumentart für das laufende Jahr
            {nummern?.jahr ? ` (${nummern.jahr})` : ""}. Damit kann der Nummernkreis beim Wechsel
            von einem anderen Programm mitten im Jahr fortgesetzt werden. Die nächste vergebene
            Nummer liegt nie unter bereits vergebenen Nummern.
          </p>
          {nummernLoading ? (
            <div className="flex items-center gap-2 py-4 text-sm text-label-secondary">
              <Spinner />
              Nummernkreise werden geladen …
            </div>
          ) : (
            <div className="space-y-4">
              {GD_NUMMER_TYPEN.map((typ) => {
                const info = nummern?.nummern?.[typ];
                const jahr = nummern?.jahr ?? new Date().getFullYear();
                const naechste = previewNaechsteNummer(
                  typ,
                  jahr,
                  startnummern[typ] ?? 1,
                  info?.last_lauf ?? 0,
                );
                return (
                  <div key={typ} className="gd-layout-config__field-group">
                    <h3>{gdNummerTypLabel(typ)}</h3>
                    <div className="gd-layout-config__grid-2">
                      <TextInput
                        label="Startnummer"
                        type="number"
                        min={1}
                        max={9999}
                        value={String(startnummern[typ] ?? 1)}
                        onChange={(e) => {
                          const value = Math.max(1, Math.min(9999, Number(e.target.value) || 1));
                          setStartnummern((prev) => ({ ...prev, [typ]: value }));
                        }}
                        hint="Erste Laufnummer, z. B. 151 → RE26-0151"
                      />
                      <div className="space-y-1 text-sm">
                        <span className="text-label-secondary">Nächste Nummer</span>
                        <p className="rounded-lg border border-separator bg-fill-tertiary px-3 py-2 font-medium text-label">
                          {naechste}
                        </p>
                        <p className="text-xs text-label-tertiary">
                          Format Präfix + Jahr + Laufnummer (vierstellig).
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
              <div className="pt-2">
                <Button
                  type="button"
                  onClick={() => void handleSaveNummern()}
                  disabled={nummernSaving || nummernLoading}
                >
                  {nummernSaving ? "Wird gespeichert …" : "Startnummern speichern"}
                </Button>
              </div>
            </div>
          )}
        </>
      );
    }

    if (section === "allgemein") {
      return (
        <>
          <p className="gd-layout-config__hint">
            Layout-Einstellungen gelten pro Dokumenttyp (Angebot, Rechnung, …). PDF-Ausgabe und
            Live-Vorschau verwenden diese Konfiguration.
          </p>
          <div className="gd-layout-config__grid-2">
            <SelectInput
              label="Dokumenttyp"
              value={dokumentTyp}
              onChange={(e) => setDokumentTyp(e.target.value as GdLayoutTyp)}
            >
              {GD_DOKUMENT_TYP_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </SelectInput>
            <SelectInput
              label="Layout von Typ kopieren"
              value={copySource === dokumentTyp ? "" : copySource}
              onChange={(e) => setCopySource(e.target.value as GdLayoutTyp)}
            >
              {GD_LAYOUT_EDITABLE_TYP_OPTIONS.filter((opt) => opt.value !== dokumentTyp).map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </SelectInput>
          </div>
          <div className="mt-4">
            <Button type="button" variant="secondary" onClick={() => void handleCopyFromType()} disabled={saving}>
              Aus Typ kopieren
            </Button>
          </div>
        </>
      );
    }

    if (section === "qr-signatur") {
      return (
        <GdQrSignaturPanel
          config={layout.qr_signatur}
          onChange={(qr_signatur) => setLayout((prev) => ({ ...prev, qr_signatur }))}
        />
      );
    }

    if (section === "gestaltung") {
      return (
        <>
          <div className="gd-layout-config__field-group">
            <h3>Layout</h3>
            <div className="gd-layout-config__grid-2">
              <SelectInput
                label="Schriftart"
                value={layout.font_family}
                onChange={(e) => setLayout((prev) => ({ ...prev, font_family: e.target.value }))}
              >
                {GD_FONT_OPTIONS.map((font) => (
                  <option key={font} value={font}>
                    {font.split(",")[0]}
                  </option>
                ))}
              </SelectInput>
              <SelectInput
                label="Schriftgröße"
                value={String(layout.font_size_pt)}
                onChange={(e) =>
                  setLayout((prev) => ({ ...prev, font_size_pt: num(e.target.value) || 10 }))
                }
              >
                {GD_FONT_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    {size} pt
                  </option>
                ))}
              </SelectInput>
              <SelectInput
                label="Zeilenabstand Adresse"
                value={String(layout.adresse_zeilenabstand)}
                onChange={(e) =>
                  setLayout((prev) => ({
                    ...prev,
                    adresse_zeilenabstand: num(e.target.value) || 1.2,
                  }))
                }
              >
                {(GD_ADRESSE_ZEILENABSTAND_OPTIONS.includes(layout.adresse_zeilenabstand)
                  ? GD_ADRESSE_ZEILENABSTAND_OPTIONS
                  : [...GD_ADRESSE_ZEILENABSTAND_OPTIONS, layout.adresse_zeilenabstand].sort(
                      (a, b) => a - b,
                    )
                ).map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </SelectInput>
              <SelectInput
                label="Zeilenabstand Einleitung/Schluss"
                value={String(layout.text_zeilenabstand)}
                onChange={(e) =>
                  setLayout((prev) => ({
                    ...prev,
                    text_zeilenabstand: num(e.target.value) || 1.15,
                  }))
                }
              >
                {(GD_TEXT_ZEILENABSTAND_OPTIONS.includes(layout.text_zeilenabstand)
                  ? GD_TEXT_ZEILENABSTAND_OPTIONS
                  : [...GD_TEXT_ZEILENABSTAND_OPTIONS, layout.text_zeilenabstand].sort(
                      (a, b) => a - b,
                    )
                ).map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </SelectInput>
            </div>
            <p className="gd-layout-config__hint mt-2">
              Adresse: Empfängerblock. Einleitung/Schluss: Text oberhalb und unterhalb der Positionen.
            </p>
            <div className="mt-3">
              <Checkbox
                label="Falzmarken auf erster Seite anzeigen"
                checked={layout.show_fold_marks}
                onChange={(e) =>
                  setLayout((prev) => ({ ...prev, show_fold_marks: e.target.checked }))
                }
              />
            </div>
          </div>
          <PositionstextFields
            config={layout.positionstext}
            onChange={(positionstext) => setLayout((prev) => ({ ...prev, positionstext }))}
          />
          <PageLayoutFields
            title="Erste Seite – Seitenränder & Hauptblock"
            layout={layout.erste_seite}
            onChange={(erste_seite) => setLayout((prev) => ({ ...prev, erste_seite }))}
          />
        </>
      );
    }

    if (section === "erste-seite") {
      return (
        <>
          <BriefpapierUpload
            label="Briefpapier erste Seite"
            briefpapierId={layout.briefpapier_erste_id}
            dokumentTyp={dokumentTyp}
            seite="erste"
            uploading={uploadingBriefpapier}
            onUploadStart={() => setUploadingBriefpapier(true)}
            onUploadEnd={() => setUploadingBriefpapier(false)}
            onUploaded={(id) => setLayout((prev) => ({ ...prev, briefpapier_erste_id: id }))}
            onRemove={() => setLayout((prev) => ({ ...prev, briefpapier_erste_id: null }))}
          />
          <PageLayoutFields
            title="Hauptblock erste Seite"
            layout={layout.erste_seite}
            onChange={(erste_seite) => setLayout((prev) => ({ ...prev, erste_seite }))}
          />
          <GdErsteSeiteContentPanel layout={layout} onChange={setLayout} />
        </>
      );
    }

    if (section === "zusaetzliche-optionen") {
      return (
        <GdZusaetzlicheOptionenPanel
          config={layout.zusaetzliche_optionen}
          onChange={(zusaetzliche_optionen) =>
            setLayout((prev) => ({ ...prev, zusaetzliche_optionen }))
          }
        />
      );
    }

    return (
      <>
        <BriefpapierUpload
          label="Briefpapier Folgeseiten"
          briefpapierId={layout.briefpapier_folge_id}
          dokumentTyp={dokumentTyp}
          seite="folge"
          uploading={uploadingBriefpapier}
          onUploadStart={() => setUploadingBriefpapier(true)}
          onUploadEnd={() => setUploadingBriefpapier(false)}
          onUploaded={(id) => setLayout((prev) => ({ ...prev, briefpapier_folge_id: id }))}
          onRemove={() => setLayout((prev) => ({ ...prev, briefpapier_folge_id: null }))}
        />
        <PageLayoutFields
          title="Folgeseiten"
          layout={layout.folgeseite}
          onChange={(folgeseite) => setLayout((prev) => ({ ...prev, folgeseite }))}
        />
      </>
    );
  }

  return (
    <AppShell title="Layout-Einstellungen">
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <Link to="/dokumente/einstellungen" className="text-sm text-accent hover:underline">
            ← Einstellungen
          </Link>
        </div>

        <div className="gd-layout-config">
        <div className="gd-layout-config__toolbar">
          <div>
            <h1 className="text-lg font-semibold text-label">
              Layout-Einstellungen für {GD_DOKUMENT_TYP_OPTIONS.find((o) => o.value === dokumentTyp)?.label}
            </h1>
            <p className="text-sm text-label-secondary">
              {isStundennachweis
                ? "Vorschau der Jinja-Vorlage (ohne Layout-Einstellungen)."
                : "Briefpapier, Seitenränder, QR-Codes und Unterschrift – mit Live-Vorschau."}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <SelectInput
              label="Dokumenttyp"
              value={dokumentTyp}
              onChange={(e) => setDokumentTyp(e.target.value as GdLayoutTyp)}
              className="min-w-[180px]"
            >
              {GD_DOKUMENT_TYP_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </SelectInput>
            {!isStundennachweis ? (
              <div className="flex items-end">
                {section === "nummern" ? (
                  <Button
                    type="button"
                    onClick={() => void handleSaveNummern()}
                    disabled={nummernSaving || nummernLoading}
                  >
                    Startnummern speichern
                  </Button>
                ) : (
                  <Button type="button" onClick={() => void handleSave()} disabled={saving || loading}>
                    Speichern
                  </Button>
                )}
              </div>
            ) : null}
          </div>
        </div>

        {error ? <Alert variant="error">{error}</Alert> : null}

        {!isStundennachweis ? (
          <nav className="gd-layout-config__nav" aria-label="Layout-Bereiche">
            {visibleSections.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`gd-layout-config__nav-item${section === item.id ? " is-active" : ""}`}
                onClick={() => setSection(item.id)}
              >
                {item.label}
              </button>
            ))}
          </nav>
        ) : null}

        <section className="gd-layout-config__panel">
          <h2 className="gd-layout-config__panel-title">{sectionTitle}</h2>
          {loading ? (
            <div className="flex items-center gap-2 py-6 text-sm text-label-secondary">
              <Spinner />
              Layout wird geladen …
            </div>
          ) : (
            renderSectionPanel()
          )}
        </section>

        <aside className="gd-layout-config__preview">
          <div className="gd-layout-config__preview-toolbar">
            <span className="gd-layout-config__preview-title">Vorschau</span>
            {!isStundennachweis ? (
              <label className="gd-layout-config__content-amount">
                <span className="gd-layout-config__content-amount-label">Inhalt</span>
                <span className="gd-layout-config__content-amount-scale">wenig</span>
                <input
                  type="range"
                  min={1}
                  max={10}
                  step={1}
                  value={contentAmount}
                  onChange={(e) => setContentAmount(Number(e.target.value) || 3)}
                  aria-label="Menge des Beispielinhalts in der Vorschau"
                />
                <span className="gd-layout-config__content-amount-scale">viel</span>
              </label>
            ) : null}
          </div>
          <div className="gd-layout-config__preview-frame-wrap">
            {!previewHasContent || !previewUrl ? (
              <div className="gd-layout-config__preview-placeholder">
                <Spinner />
                <span>Vorschau wird vorbereitet …</span>
              </div>
            ) : (
              <div className="gd-layout-config__preview-stage">
                <iframe
                  title="Layout-Vorschau"
                  src={previewUrl}
                  className={`gd-layout-config__preview-frame${previewSyncing ? " is-syncing" : ""}`}
                />
                {previewSyncing ? (
                  <div className="gd-layout-config__preview-sync" aria-hidden="true">
                    <Spinner />
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </aside>
      </div>
      </div>
    </AppShell>
  );
}
